import { toast } from '@/lib/toast'

export type ProductPricing = {
  unitPrice: number
  originalUnitPrice: number | null
  discountApplied: boolean
}

export const FREEFORM_ADDRESS_CHANNELS = ['store', 'messenger', 'instagram']

export const STATUS_TONES: Record<string, { bg: string, fg: string, dot: string, ring: string }> = {
  pending: { bg: '#fffbeb', fg: '#92400e', dot: '#f59e0b', ring: '#fbbf24' },
  // Derived, never stored — see resolveOrderStatus.
  confirmed: { bg: '#f0fdf4', fg: '#15803d', dot: '#16a34a', ring: '#86efac' },
  paid: { bg: '#eff6ff', fg: '#1d4ed8', dot: '#3b82f6', ring: '#93c5fd' },
  ready: { bg: '#eff6ff', fg: '#1d4ed8', dot: '#3b82f6', ring: '#93c5fd' },
  shipped: { bg: '#eff6ff', fg: '#1d4ed8', dot: '#3b82f6', ring: '#93c5fd' },
  delivered: { bg: '#f0fdf4', fg: '#15803d', dot: '#16a34a', ring: '#86efac' },
  canceled: { bg: '#fef2f2', fg: '#b91c1c', dot: '#dc2626', ring: '#fca5a5' },
  refunded: { bg: '#f1f5f9', fg: '#475569', dot: '#64748b', ring: '#cbd5e1' },
  partiallyRefunded: { bg: '#fdf2f8', fg: '#be185d', dot: '#ec4899', ring: '#f9a8d4' },
  deliveryFailed: { bg: '#fef2f2', fg: '#b91c1c', dot: '#dc2626', ring: '#fca5a5' },
}

export const STATUS_LABELS: Record<string, string> = {
  pending: 'Төлбөр хүлээгдэж буй',
  confirmed: 'Баталгаажсан',
  paid: 'Төлбөр баталгаажсан',
  ready: 'Бэлэн боллоо',
  shipped: 'Хүргэлтэнд гарсан',
  delivered: 'Хүргэгдсэн',
  canceled: 'Цуцлагдсан',
  refunded: 'Бүтэн буцаасан',
  partiallyRefunded: 'Хагас буцаасан',
  deliveryFailed: 'Хүргэлт амжилтгүй',
}

export const TERMINAL_STATUSES = new Set(['canceled', 'refunded', 'deliveryFailed'])

// True when the vendor has an active, verified Booklo enrollment. Booklo supplies
// the courier and the fee, so an enrolled vendor offers delivery even when their own
// deliveryType is 'noDelivery' — that setting only means they don't deliver themselves.
// Mirrors isProviderVerified in the backend's lib/deliveryProvider.util.js.
export function isBookloActive(deliveryProvider: any): boolean {
  return deliveryProvider?.provider === 'booklo' && deliveryProvider?.status === 'verified' && deliveryProvider?.isActive === true
}

// Lifecycle order used to decide whether a Booklo delivery status is "further along"
// than order.status.
const STATUS_RANK: Record<string, number> = { pending: 0, paid: 1, ready: 2, shipped: 3, delivered: 4 }
// Booklo statusCode → the order status it implies. Intermediate delivery states
// (in_delivery / not_arrived / postponed) all map to 'shipped'.
const BOOKLO_STATUS_TO_ORDER: Record<string, string> = {
  in_delivery: 'shipped',
  not_arrived: 'shipped',
  postponed: 'shipped',
  delivered: 'delivered',
  delivery_failed: 'deliveryFailed',
  canceled: 'canceled',
}

// Resolve the status to DISPLAY for an order: for a Booklo-dispatched order whose
// synced Booklo status is further along the lifecycle than order.status, prefer the
// Booklo-implied status (the sync cron may not have transitioned order.status yet).
// Only advances forward; never overrides a terminal local status.
export function bookloDisplayStatus(order: any, baseStatus: string): string {
  if (!order?.bookloOrderId || !order?.bookloStatus?.statusCode) return baseStatus
  if (TERMINAL_STATUSES.has(baseStatus)) return baseStatus
  const mapped = BOOKLO_STATUS_TO_ORDER[order.bookloStatus.statusCode]
  if (!mapped) return baseStatus
  // Terminal Booklo states (failed/canceled) always win; forward states only if further.
  if (TERMINAL_STATUSES.has(mapped)) return mapped
  return (STATUS_RANK[mapped] ?? -1) > (STATUS_RANK[baseStatus] ?? -1) ? mapped : baseStatus
}

// "Бэлэн болсон" is an opt-in vendor workflow (settings.canReadyStatus). Vendors who
// leave it off never move an order through 'ready', so the step must not appear in the
// customer's timeline as a stage that will never complete. Orders that DID pass through
// it keep the step — a vendor can toggle the setting off later, and dropping the step an
// order is currently sitting on would snap its stepper back to the first stage.
export function showsReadyStep(order: any, vendor: any): boolean {
  return !!vendor?.canReadyStatus || !!order?.readyAt || order?.status === 'ready'
}

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  qpay: 'QPay',
  storepay: 'StorePay зээл',
  pocket: 'PocketZero зээл',
  lend: 'LendMN зээл',
  omniway: 'OmniWay зээл',
  transfer: 'Данс шилжүүлэг',
  cash: 'Бэлнээр',
  // Placeholder a pay-after-delivery order carries until a rail is chosen at the door.
  onDelivery: 'Барааг авахдаа төлөх',
}

export const DIGITAL_DOWNLOAD_STATUSES = new Set(['paid', 'ready', 'shipped', 'delivered'])

/* ================= Split payment (урьдчилгаа) ================= */

/*
  Shared vocabulary. Lives here rather than inline in JSX because the three templates
  (default, dark, pill) each render their own markup and would otherwise drift — the
  arithmetic and the wording are the two things that must not.

  Deliberately NOT folded into STATUS_LABELS: a deposit is not an order status, and
  STATUS_TONES is keyed in lockstep with that map.
*/
export const DEPOSIT_LABELS = {
  deposit: 'Урьдчилгаа',
  balance: 'Үлдэгдэл',
  payNow: 'Одоо төлөх',
  payLater: 'Дараа төлөх',
  depositPaid: 'Урьдчилгаа төлсөн',
  depositPending: 'Урьдчилгаа хүлээгдэж буй',
  balanceDue: 'Үлдэгдэл төлбөр',
  payBalance: 'Үлдэгдэл төлөх',
  payDeposit: 'Урьдчилгаа төлөх',
  terms: 'Урьдчилгааны нөхцөл',
  noBalance: 'Үлдэгдэл төлөх шаардлагагүй',
}

// Mirrors MIN_PAYABLE_AMOUNT in the backend's lib/payment.constants.js. The server is
// authoritative; this is a UX affordance so we do not offer a leg no rail will accept.
export const DEPOSIT_MIN_LEG = 50

export function isDepositOrder(order: any) {
  return !!(order?.depositAmount > 0)
}

export function getDepositPaid(order: any) {
  return order?.depositPaidAt && order?.depositAmount > 0 ? order.depositAmount : 0
}

/*
  What this order is asking to be paid RIGHT NOW.

  Every payment block hero figure goes through this. Reading order.amount directly would
  show a customer the full total on a screen that is only charging them the deposit.
*/
export function getPayableAmount(order: any) {
  const amount = order?.amount || 0
  if (!order?.depositAmount) return amount
  if (!order.depositPaidAt) return order.depositAmount
  if (!order.paidAt) return Math.max(0, amount - order.depositAmount)
  return amount
}

/*
  The deposit/balance split for display.

  Kept separate from calcPriceBreakdown, which is purely discount decomposition — the two
  answer different questions and entangling them makes both harder to change.

  The balance is clamped against totalRefunded as well as the deposit: a partial refund
  can take an order below what has already been handed over, and a negative "still owed"
  is worse than nonsense, it is a prompt to collect money that is not due.
*/
/*
  Does the order carry an invoice somebody is currently waiting on? An abandoned
  PocketZero blob does not count — it is kept only so a late callback can still resolve
  the order (PocketZero has no cancel API), not because a rail is live.
*/
export function hasLiveInvoice(order: any) {
  const livePocket = order?.pocket?.id && !order.pocket.abandonedAt
  const liveOmniway = order?.omniway?.invoiceNumber && !order.omniway.canceledAt
  return !!(order?.qpay?.id || order?.storepay?.id || livePocket || order?.lend?.id || liveOmniway)
}

// OmniWay returns its own QR as base64; some responses omit the data-URI prefix.
export function getOmniwayQrImage(order: any): string {
  const image = order?.omniway?.imageBase64
  if (!image) return ''
  return image.startsWith('data:') ? image : `data:image/png;base64,${image}`
}

/*
  The status a customer should SEE, which is not always order.status.

  'pending' means "waiting for money" — true of a normal unpaid order, and of a
  split-payment order whose deposit has not arrived. It is NOT true of an order where
  nothing is owed up front: a pay-after-delivery order, or a deposit order whose deposit
  has landed. Those are confirmed; the goods simply have not moved yet. Labelling them
  "Төлбөр хүлээгдэж буй" tells a customer who has just paid that their money never arrived.

  payAfterDelivery is exactly that condition — set at checkout for onDelivery orders, and
  flipped by settleDeposit when a deposit lands — so one test covers both cases.

  Derived, never stored: 'confirmed' is not a fulfilment state (the order still has to go
  ready -> shipped -> delivered), it is the payment axis reporting satisfied. Putting it
  in the orderStatus enum would place a non-fulfilment value on the fulfilment ladder.
*/
export function resolveOrderStatus(order: any, displayStatus?: string): string {
  const status = displayStatus ?? order?.status
  if (status !== 'pending') return status
  if (order?.payAfterDelivery && !order?.paidAt) return 'confirmed'
  return 'pending'
}

export function calcDepositBreakdown(order: any) {
  const isDeposit = isDepositOrder(order)
  const depositAmount = order?.depositAmount || 0
  const paidInFull = !!order?.paidAt
  const depositPaid = getDepositPaid(order)
  const netAmount = Math.max(0, (order?.amount || 0) - (order?.totalRefunded || 0))
  return {
    isDeposit,
    depositAmount,
    depositPaidAt: order?.depositPaidAt || null,
    isDepositPaid: !!order?.depositPaidAt,
    // Deposit taken but the balance still outstanding — the state a pre-order sits in
    // for the whole 7-10 day lead time.
    isAwaitingBalance: isDeposit && !!order?.depositPaidAt && !paidInFull,
    isDepositDue: isDeposit && !order?.depositPaidAt && !paidInFull,
    isFullyPaid: paidInFull,
    paidSoFar: paidInFull ? netAmount : depositPaid,
    // How much is STILL OWED — collapses to 0 on settlement, which is what gates the
    // "pay the balance" UI.
    outstanding: paidInFull ? 0 : Math.max(0, netAmount - depositPaid),
    // What the balance LEG is worth, paid or not. The breakdown is a record of the two
    // legs, so showing 0 there once settled erases what the customer actually handed over.
    balanceAmount: Math.max(0, netAmount - depositAmount),
  }
}

export function formatStepDate(value: any) {
  if (!value) return '—'
  const d = new Date(value)
  if (isNaN(d.getTime())) return '—'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} · ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function formatAddressType(type: string) {
  if (type === 'apartment') return 'Орон сууц'
  if (type === 'house') return 'Хашаа байшин'
  if (type === 'office') return 'Оффис'
}

export function formatAddressDetails(address: any) {
  if (!address) return ''
  const addressType = address.type || 'apartment'
  if (addressType === 'apartment') return `${address.bair}-р байр, тоот ${address.toot}`
  if (addressType === 'house') return `${address.street ? `${address.street}, ` : ''}${address.houseNum}`
  if (addressType === 'office') return `${address.building}, ${address.floor}-р давхар, тоот ${address.toot}${address.officeName ? ` (${address.officeName})` : ''}`
  return ''
}

export function formatOrderAddress(address: any, channel?: string) {
  if (!address) return 'Хаяг байхгүй'
  if (channel && FREEFORM_ADDRESS_CHANNELS.includes(channel)) {
    const parts = [address.city, address.duureg, address.khoroo, address.additionalAddress].filter(Boolean)
    return parts.length ? parts.join(', ') : 'Хаяг байхгүй'
  }
  const location = `${address.city || ''}, ${address.duureg || ''}, ${address.khoroo || ''}`
  const addressType = address.type || 'apartment'
  let details = ''
  if (addressType === 'apartment') {
    details = `${address.bair || ''}-р байр, тоот ${address.toot || ''}`
  } else if (addressType === 'house') {
    details = `${address.street ? `${address.street}, ` : ''}${address.houseNum}`
  } else if (addressType === 'office') {
    details = `${address.building || ''}, ${address.floor || ''}-р давхар, тоот ${address.toot || ''}${address.officeName ? ` (${address.officeName})` : ''}`
  }
  const additionalInfo = address.additionalAddress ? `, ${address.additionalAddress}` : ''
  return `${location}, ${details}${additionalInfo}`
}

export function getCustomerName(order: any) {
  return order?.fullName
    || (order?.firstName && order?.lastName ? `${order.firstName} ${order.lastName}` : order?.firstName)
    || ''
}

export function getVariantDimension(v: any) {
  if (!v) return null
  if (v.weight && v.weight > 0) return `${v.weight}${v.weightUnit || 'гр'}`
  if (v.height && v.height > 0) return `${v.height}${v.heightUnit || 'см'} өндөр`
  if (v.length && v.length > 0) return `${v.length}${v.lengthUnit || 'см'} урт`
  if (v.width && v.width > 0) return `${v.width}${v.widthUnit || 'см'} өргөн`
  return null
}

export function isCustomFieldVisible(f: any) {
  if (f.value === null || f.value === undefined || f.value === '') return false
  if (f.type === 'checkbox' && f.value === false) return false
  return true
}

export function getProductPricing(product: any): ProductPricing {
  const variant = product.variant
  const isReward = product.isRewardItem
  const discount = !isReward ? product.discount : null
  const hasDiscount = discount && (discount.type === 'productDiscount' || discount.type === 'order')

  if (isReward) {
    return {
      unitPrice: product.price ?? 0,
      originalUnitPrice: product.originalPrice || variant?.price,
      discountApplied: false,
    }
  }
  if (hasDiscount && discount.discountAmount) {
    const salePrice = variant?.priceSale && variant.priceSale > 0 && variant.priceSale < variant?.price ? variant.priceSale : null
    const basePrice = discount.discountBase === 'sale' && salePrice !== null ? salePrice : variant?.price
    const discountedPrice = Math.max(0, basePrice - discount.discountAmount)
    if (discount.discountBase === 'original' && salePrice !== null) {
      return {
        unitPrice: Math.min(discountedPrice, salePrice),
        originalUnitPrice: variant?.price,
        discountApplied: discountedPrice <= salePrice,
      }
    }
    return { unitPrice: discountedPrice, originalUnitPrice: variant?.price, discountApplied: true }
  }
  if (variant?.priceSale && variant.priceSale < variant?.price) {
    return { unitPrice: variant.priceSale, originalUnitPrice: variant.price, discountApplied: false }
  }
  return { unitPrice: variant?.price || 0, originalUnitPrice: null, discountApplied: false }
}

export function calcPriceBreakdown(order: any) {
  let salePriceSavings = 0
  const breakdownMap: Record<string, { id: string, name: string, amount: number }> = {}
  const addToBreakdown = (id: string, name: string, amount: number) => {
    if (!breakdownMap[id]) breakdownMap[id] = { id, name, amount: 0 }
    breakdownMap[id].amount += amount
  }
  for (const p of (order?.products || [])) {
    const v = p.variant
    const qty = p.quantity || 0
    if (p.isRewardItem) {
      const saving = ((p.originalPrice || v?.price || 0) - (p.price || 0)) * qty
      if (saving > 0 && p.discountId) addToBreakdown(p.discountId, p.discountName || 'Урамшуулал', saving)
      continue
    }
    const pSaleSavings = v?.priceSale && v.priceSale > 0 && v.priceSale < v.price ? (v.price - v.priceSale) * qty : 0
    if (p.discount && p.discount.discountAmount > 0) {
      const dAmount = p.discount.discountAmount * qty
      if (p.discount.discountBase === 'sale' && pSaleSavings > 0) {
        salePriceSavings += pSaleSavings
        if (dAmount > 0) addToBreakdown(p.discount.id, p.discount.name || 'Урамшуулал', dAmount)
      } else if (dAmount >= pSaleSavings) {
        addToBreakdown(p.discount.id, p.discount.name || 'Урамшуулал', dAmount)
      } else {
        salePriceSavings += pSaleSavings
      }
    } else {
      salePriceSavings += pSaleSavings
    }
  }
  for (const d of (order?.discounts || [])) {
    if (d.amount > 0 && !breakdownMap[d.id]) {
      addToBreakdown(d.id, d.name || 'Урамшуулал', d.amount)
    }
  }
  return {
    salePriceSavings,
    discountBreakdown: Object.values(breakdownMap).filter(b => b.amount > 0),
  }
}

export function buildDigitalDownloadUrl(order: any, vendor: any, variantId: string) {
  if (!order?.customId || !order?.phone || !vendor?.id) return null
  const params = new URLSearchParams({
    customId: String(order.customId),
    phone: String(order.phone),
    vendorId: String(vendor.id),
  })
  return `/api/digital-download/${encodeURIComponent(variantId)}?${params.toString()}`
}

export async function downloadDigitalFile(url: string) {
  let res: Response
  try {
    res = await fetch(url, { method: 'GET', cache: 'no-store' })
  } catch {
    toast.error('Сервертэй холбогдоход алдаа гарлаа')
    return
  }

  const contentType = res.headers.get('content-type') || ''
  if (!res.ok || contentType.includes('application/json')) {
    const body = await res.json().catch(() => null)
    toast.error(body?.error || 'Файл татахад алдаа гарлаа')
    return
  }

  const disposition = res.headers.get('content-disposition') || ''
  let filename = 'download'
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i)
  if (utf8Match?.[1]) {
    try { filename = decodeURIComponent(utf8Match[1]) } catch { filename = utf8Match[1] }
  } else if (plainMatch?.[1]) {
    filename = plainMatch[1]
  }

  const blob = await res.blob()
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}
